import { HttpInterceptorFn, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, throwError, EMPTY } from 'rxjs';
import { SessionRevokedService } from '../services/session-revoked.service';
import { AuthService } from '../services/auth.service';
import { ModalPriorityService } from '../services/modal-priority.service';
import { BroadcastChannelService } from '../services/broadcast-channel.service';
import { isCasePermissionDeniedError } from '../utils/case-permissions.util';

/**
 * HTTP interceptor to handle session/account and access errors:
 * - Other 403: redirect to /noaccess (permission denied)
 */
export const sessionRevokedInterceptor: HttpInterceptorFn = (req, next) => {
  const sessionRevokedService = inject(SessionRevokedService);
  const authService = inject(AuthService);
  const modalPriorityService = inject(ModalPriorityService);
  const broadcastChannelService = inject(BroadcastChannelService);
  const router = inject(Router);

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      // Only handle SESSION_REVOKED code (tokenVersion mismatch)
      // Token expiration errors from jwt.verify are NOT caught here
      if (error.status === 401 && error.error?.code === 'SESSION_REVOKED') {
        // Set priority - this will close token expiration modal if it's showing
        modalPriorityService.showSessionRevoked();
        sessionRevokedService.showSessionRevokedModal();
        // Broadcast to other tabs
        broadcastChannelService.postMessage(
          broadcastChannelService.MESSAGE_TYPES.SESSION_REVOKED
        );
        // Stop error propagation - return empty observable to prevent other error handlers from showing toasts
        return EMPTY;
      }

      // Handle ACCOUNT_DISABLED code (highest priority)
      if (error.status === 403 && error.error?.code === 'ACCOUNT_DISABLED') {
        // Set highest priority - this will close token expiration and session revoked modals
        modalPriorityService.showAccountDisabled();
        const date = error.error?.date || new Date().toISOString();
        authService.showAccountInactiveModal(date);
        // Broadcast to other tabs
        broadcastChannelService.postMessage(
          broadcastChannelService.MESSAGE_TYPES.ACCOUNT_DISABLED,
          { date }
        );
        return EMPTY;
      }

      // Generic 403 Forbidden: redirect to noaccess (permission denied)
      if (error.status === 403) {
        if (
          error.error?.code === 'PASSWORD_RESET_REQUIRED' ||
          error.error?.code === 'PASSWORD_EXPIRED'
        ) {
          const u = authService.getCurrentUser();
          const id = (u?.email || u?.username || '').trim();
          if (id) {
            authService.setPasswordResetRequired(id);
          } else {
            authService.setPasswordResetRequired();
          }
          const currentUrl = router.url;
          if (!currentUrl.includes('/change-password-required')) {
            router.navigate(['/change-password-required']);
          }
          return throwError(() => error);
        }

        // DBM tenant mismatch: tenant exists in app but not in DBM – let the caller show a friendly message, do not redirect
        const msg = error.error?.message ?? error.error?.error ?? '';
        const rawError = typeof error.error?.error === 'string' ? error.error.error : '';
        const isDbmTenantMismatch =
          (typeof msg === 'string' && msg.toLowerCase().includes('tenant mismatch')) ||
          (typeof rawError === 'string' && rawError.toLowerCase().includes('tenant mismatch'));
        if (isDbmTenantMismatch) {
          return throwError(() => error);
        }

        const requestUrl = req.url || '';

        // GET /api/roles requires ROLES_READ; 403 means no access to Roles only – do not redirect whole app to noaccess
        if (requestUrl.includes('/api/roles')) {
          return throwError(() => error);
        }

        // Case assignment permission denials: let the caller show a toast on the case page
        if (requestUrl.includes('/api/case') && isCasePermissionDeniedError(msg)) {
          return throwError(() => error);
        }

        const currentUrl = router.url;
        const isOnAuthPage = currentUrl === '/' ||
                             currentUrl.includes('/signin') ||
                             currentUrl.includes('/signup');
        const isAuthRelatedRequest = requestUrl.includes('/api/auth/') ||
                                     requestUrl.includes('/api/users/me');
        if (!currentUrl.includes('/noaccess') && !isOnAuthPage && !isAuthRelatedRequest) {
          router.navigate(['/noaccess']);
        }
      }

      // For all other errors, pass through normally
      return throwError(() => error);
    })
  );
};
